import { useListCouponMasters } from '@/queries'

export const COUPON_TYPES = {
	CAFE_TICKET: 'cafe_ticket',
	COMPANION_TICKET: 'companion_ticket',
	GOODS_DISCOUNT_TICKET: 'goods_discount_ticket',
	BIRTHDAY_CAFE_TICKET: 'birthday_cafe_ticket',
	DISCOUNT_COUPON_1000: 'discount_coupon_1000',
	PRESENT_COUPON: 'present_coupon',
} as const

export type CouponType = (typeof COUPON_TYPES)[keyof typeof COUPON_TYPES]

export const getCouponTypeLabel = (couponType: string | null | undefined): string => {
	const normalizedType = (couponType || '').trim().toLowerCase().replaceAll('-', '_')

	switch (normalizedType) {
		case COUPON_TYPES.CAFE_TICKET:
			return 'カフェチケット'
		case COUPON_TYPES.COMPANION_TICKET:
			return '同伴者チケット'
		case COUPON_TYPES.GOODS_DISCOUNT_TICKET:
			return 'グッズ割引チケット'
		case COUPON_TYPES.BIRTHDAY_CAFE_TICKET:
			return 'お誕生月カフェチケット'
		case COUPON_TYPES.DISCOUNT_COUPON_1000:
			return '1,000円割引クーポン'
		case COUPON_TYPES.PRESENT_COUPON:
			return 'プレゼントクーポン'
		default:
			return normalizedType || 'ー'
	}
}

export const getCouponTitle = (couponType: string, title?: string): string => {
	if (title) {
		return title
	}
	return getCouponTypeLabel(couponType)
}

export const getCouponStatusColor = (status: string): string => {
	switch (status) {
		case 'unused':
			return 'green'
		case 'used':
			return 'blue'
		case 'expired':
			return 'red'
		default:
			return 'default'
	}
}

export const getCouponStatusLabel = (status: string): string => {
	switch (status) {
		case 'unused':
			return '未使用'
		case 'used':
			return '使用済み'
		case 'expired':
			return '期限切れ'
		default:
			return status || 'ー'
	}
}

export const COUPON_OPTIONS = [
	{ value: COUPON_TYPES.CAFE_TICKET, label: 'カフェチケット' },
	{ value: COUPON_TYPES.COMPANION_TICKET, label: '同伴者チケット' },
	{ value: COUPON_TYPES.GOODS_DISCOUNT_TICKET, label: 'グッズ割引チケット' },
	{ value: COUPON_TYPES.BIRTHDAY_CAFE_TICKET, label: 'お誕生月カフェチケット' },
	{ value: COUPON_TYPES.DISCOUNT_COUPON_1000, label: '1,000円割引クーポン' },
	{ value: COUPON_TYPES.PRESENT_COUPON, label: 'プレゼントクーポン' },
]

export const filterCouponsByCategory = <T extends { couponType: string }>(
	coupons: T[],
	categories: string[],
): T[] => {
	return coupons.filter((coupon) => categories.includes(coupon.couponType))
}

export const getCouponIssueReasonLabel = (reason: string | null): string => {
	switch (reason) {
		case 'new_member':
			return '新規会員'
		case 'birthday':
			return '誕生日特典'
		case 'admin_triggered':
			return '管理者発行'
		case 'point_milestone':
			return 'ポイント特典'
		case 'campaign':
			return 'キャンペーン'
		case 'renewal':
			return '更新'
		case 'migration':
			return 'データ移行'
		default:
			return reason ? reason.replace('_', ' ') : 'ー'
	}
}

export const useCouponMasterUtils = () => {
	const { data: mastersResponse, isLoading } = useListCouponMasters()
	const masters = mastersResponse?.body?.data || []

	const getDynamicCouponTypeLabel = (couponType: string | number | null | undefined): string => {
		const master = masters.find(
			(m) =>
				m.couponMasterId === couponType ||
				m.couponMasterId === Number(couponType) ||
				m.couponType === couponType,
		)
		if (master) return master.title
		return getCouponTypeLabel(String(couponType))
	}

	const getDynamicCouponTitle = (couponType: string | number, title?: string): string => {
		if (title) return title
		return getDynamicCouponTypeLabel(couponType)
	}

	const dynamicCouponOptions =
		masters.length > 0
			? masters.map((m) => ({ value: m.couponMasterId as number | string, label: m.title }))
			: (COUPON_OPTIONS as unknown as { value: number | string; label: string }[])

	return {
		masters,
		isLoading,
		getCouponTypeLabel: getDynamicCouponTypeLabel,
		getCouponTitle: getDynamicCouponTitle,
		COUPON_OPTIONS: dynamicCouponOptions,
		getCouponStatusColor,
		getCouponStatusLabel,
		getCouponIssueReasonLabel,
		filterCouponsByCategory,
	}
}
